// キャラを描く（2026-10-21 に app.js から分けた stage.ts の描く部分。2026-10-23 に中身へ）。
// 描き方はエンジン（クラシック版）と同じ charat.draw.js の関数。描く先は attachLayers・useLayers で渡したキャラの SVG とレイヤーの g。
// 画面ごとの違い（クラシック版の画面の拡大・d3 の動き）は C.scale と C.hooks（model.ts）で。why は何の操作で描き直すか
import { C, catByKey, layerG } from './model.js';
import { SIZE } from '../config.js';
import { el, keyOf } from './util.js';
import { emit } from './events.js';
import { PATTERN_PARTS_COLOR, PATTERN_REV_PARTS, PTN_ADJUST, PTN_EX1, PTN_EX2, PTN_SIZE, PTN_STROKE1, PTN_STROKE2, canPattern, classicPatternOptions, findPattern, loadPatterns, patternDefs, removePatternDefs } from './pattern.js';
import { findPart, layersOf } from './data.js';
import type { Category, PartState } from '../types.js';
import type { LayerPlace, MotionInfo, Why } from './model.js';

// 描く先: キャラの SVG（柄の defs・うすさを置く）と、その中のレイヤーを並べる g（本物と同じ g#chaSvgMake）。
// レイヤーの g（g#lcart_{レイヤー名}）をデータの重ね順で作って make に入れる（新しい画面）
export function attachLayers(svg: SVGSVGElement, make: SVGGElement) {
  C.svg = svg;
  C.data.LAYER.forEach((name) => {
    const g = el<SVGGElement>('g', { id: 'lcart_' + name });
    layerG[name] = g;
    make.appendChild(g);
  });
}
// すでにあるレイヤーの g を描く先にする（クラシック版。背景のレイヤーだけ別の SVG にある）。find はレイヤー名から g を返す
export function useLayers(svg: SVGSVGElement, find: (name: string) => SVGGElement | null) {
  C.svg = svg;
  C.data.LAYER.forEach((name) => {
    const g = find(name);
    if (g) layerG[name] = g;
  });
}
export function opacityOf(st: PartState | undefined) { const v = st && st.opacity !== '' && st.opacity != null ? Number(st.opacity) : 1; return isNaN(v) ? 1 : Math.max(0, Math.min(1, v)); }
// パーツの g の拡大を付ける・外す（差しこみ口があればそちらで）
function setZoom(node: Element, value: string | null, why: Why, cat: Category, scale: number, info: MotionInfo = {}) {
  if (C.hooks.zoomTransform) { C.hooks.zoomTransform(node, value, why, cat, scale, info); return; }
  if (value !== null) node.setAttribute('transform', value);
  else node.removeAttribute('transform');
}
// 1カテゴリ分を描き直す（パーツを入れ、拡大・移動・反転・回転・うすさ・色・グラデーション・柄を掛ける）
export function render(key: string, why: Why = 'set') {
  const cat = catByKey[key], st = C.state[key];
  if (!cat) return;
  cat.LAYER.forEach((name) => { if (layerG[name]) layerG[name].textContent = ''; });
  const part = st && st.parts != null ? findPart(cat, st.parts) : null;
  if (!part) return;
  const zoom = cat.IS_ZOOM != 0 ? Number(st.scale) || 1 : 1;
  layersOf(cat, part).forEach((src, i) => {
    const g = layerG[cat.LAYER[i]];
    if (!g || !src) return;
    const node = charatDraw.putPart(g, src); // グラデーションの id はキャラ側だけ付け替えて入れる
    if (zoom !== 1) setZoom(node, charatDraw.zoomTransform(zoom, SIZE, SIZE), why, cat, zoom);
  });
  place(cat, st, why);
  paint(cat, st);
  emit('drawn', cat, st, why);
}
// 移動・反転・回転・うすさ（レイヤーの g に付ける。動かしている間はこれだけ付け直す）
//   回転（rotate、度）: エンジンと同じく、カテゴリの全レイヤーを合わせた範囲のまん中（レイヤーの座標）で rotate(角度, cx, cy) を最後に付ける
//   （反転していると鏡に映るので、見た目の向きは逆になる）。まん中は拡大を掛けたあとの範囲で見る
//   うすさ: エンジンと同じくレイヤーの g に（くちは .lip の所だけ。CHOCO で今うすさを変えられるのは OPACITY_KEYS だけ）
//   画面の拡大 C.scale（クラシック版）は、位置と倍率に掛ける（エンジンの moveG・reverseG と同じ式）
export function place(cat: Category, st: PartState, why: Why = 'set', info: MotionInfo = {}) {
  const layers = cat.LAYER.map((name) => layerG[name] || null);
  const op = opacityOf(st), p = placeOf(cat, st);
  cat.LAYER.forEach((name, i) => {
    const g = layers[i];
    if (!g) return;
    if (C.hooks.layerTransform) C.hooks.layerTransform(g, p, why, cat, name, info);
    else g.setAttribute('transform', charatDraw.layerTransform(p.x, p.y, p.sx, p.sy, p.rot, p.c));
    charatDraw.opacityLayer(C.svg, g, name, op);
  });
  emit('placed', cat, st, why);
}
// 1カテゴリの位置・倍率・回転（place が付けるもの。クラシック版の反転の動きは、反転する前のこれから始める）
export function placeOf(cat: Category, st: PartState): LayerPlace {
  const rev = Number(st.rev) === 1 && (cat.REVERSE == 1 || cat.IS_REV != 0);
  const tx = cat.IS_MOVE != 0 ? Number(st.tx) || 0 : 0, ty = cat.IS_MOVE != 0 ? Number(st.ty) || 0 : 0;
  const rot = Number(st.rotate) || 0, c = rot ? charatDraw.rotateCenter(cat.LAYER.map((name) => layerG[name] || null)) : null;
  const k = C.scale;
  return { x: k * (tx + (rev ? SIZE : 0)), y: k * ty, sx: (rev ? -1 : 1) * k, sy: k, rot: rot, c: c };
}
// 色・グラデーション・柄（エンジンの読み込みと同じ charat.draw.js の colorLine・patternLine）
//   左右の色（CHOCO では め）: 全体の色を塗ったあと、左・右の側だけ lfrom・rfrom で塗り直す（全体の色があるときだけ）
//   柄（ptn）: 基本色・影色の所を、パーツの色（無ければ元の色）で塗った柄に（pattern.ts の PATTERN_PARTS_COLOR・PATTERN_REV_PARTS）。
//   柄をまだ読んでいなければ、読んでから描き直す
export function paint(cat: Category, st: PartState) {
  const layers = cat.LAYER.map((n) => layerG[n] || null);
  if (C.classicPattern) {
    // クラシック版（段階 B4）: エンジンの読み込み（chibi.setPartsColorPattern）と同じ。柄の一覧・defs・描き方はエンジンのもの、
    // 柄の可否（CMODE）は見ない・柄が無くても色の入れかえはする・defs の柄は消さない（エンジンも消さない）
    charatDraw.colorLine(layers, cat, st);
    // 第1世代の柄（mono）は柄の作りが違うので、柄と色の入れかえはエンジンが掛ける（chibi.applyEnginePatterns）
    if (!C.classicPattern.engineDraws) charatDraw.patternLine(layers, cat, st.ptn || '', Number(st.prev) || 0, Number(st.ptnmode) || 0, st.col, classicPatternOptions(cat));
    return;
  }
  const wantPtn = !!st.ptn && canPattern(cat), ptn = wantPtn ? findPattern(st.ptn) : null;
  if (wantPtn && !C.patterns) {
    const key = keyOf(cat);
    loadPatterns().then(() => { if (C.state[key] === st) render(key, 'pattern'); }, () => {});
  }
  if (!ptn) removePatternDefs(cat);
  charatDraw.colorLine(layers, cat, st);
  charatDraw.patternLine(layers, cat, ptn ? st.ptn : '', ptn ? Number(st.prev) || 0 : 0, Number(st.ptnmode) || 0, st.col, {
    defs: ptn ? patternDefs() : null, list: C.patterns ? C.patterns.map((p) => p.g) : null, size: PTN_SIZE, adjust: PTN_ADJUST,
    ex1: PTN_EX1, ex2: PTN_EX2, stroke1: PTN_STROKE1, stroke2: PTN_STROKE2, partsColor: PATTERN_PARTS_COLOR, revParts: PATTERN_REV_PARTS
  });
}
// 大きさだけ付け直す（render のようにパーツを作り直さない。つまみを動かしている間）
export function applyZoom(cat: Category, st: PartState, why: Why = 'drag', info: MotionInfo = {}) {
  const zoom = cat.IS_ZOOM != 0 ? Number(st.scale) || 1 : 1;
  cat.LAYER.forEach((n) => {
    const node = layerG[n] && layerG[n].firstElementChild;
    if (!node) return;
    setZoom(node, zoom !== 1 ? 'translate(' + (SIZE * zoom - SIZE) * -0.5 + ',' + (SIZE * zoom - SIZE) * -0.5 + ') scale(' + zoom + ')' : null, why, cat, zoom, info);
  });
}
export function renderAll(why: Why = 'set') { C.cats.forEach((c) => { render(keyOf(c), why); }); emit('drawnAll', why); }
